import type { StaticImageData } from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import cloudflareLoader from '@/utils/images/cloudflareLoader';
import BannersSkeleton from './BannersSkeleton';

// --- Types ---

export interface BetterPictureProps {
  src: string | StaticImageData;
  srcMobile?: string | StaticImageData;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  quality?: number;
  fetchPriority?: 'high' | 'auto';
  priority?: boolean;
}

// --- Helpers ---

// q=100 makes Cloudflare emit near-lossless AVIF (~8x heavier than q=90) with no visible gain.
const DEFAULT_BANNER_QUALITY = 90;

// Rendered banner widths (CSS px) the CDN variants are generated for.
const DESKTOP_BASE_WIDTH = 1133;
const MOBILE_BASE_WIDTH = 328;
const DESKTOP_DENSITIES = [1, 2];
const MOBILE_DENSITIES = [1, 2, 3];

const buildSrcSet = (src: string, baseWidth: number, densities: number[], quality: number) =>
  densities
    .map((density) => {
      const width = baseWidth * density;
      return `${cloudflareLoader({ src, width, quality })} ${width}w`;
    })
    .join(', ');

const getImageSrc = (src: string | StaticImageData): string =>
  typeof src === 'string' ? src : src.src;

// --- Component ---

export const BetterPicture = ({
  src,
  srcMobile,
  alt,
  className,
  width,
  height,
  priority,
  fetchPriority = 'auto',
  quality = DEFAULT_BANNER_QUALITY,
  ...props
}: BetterPictureProps) => {
  const srcString = getImageSrc(src);
  const srcMobileString = srcMobile ? getImageSrc(srcMobile) : undefined;

  // Priority (LCP) banners are visible straight from SSR: hiding them until hydration + onLoad
  // delays LCP by the whole JS boot time. Other banners keep the skeleton fade-in.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(priority ? srcString : null);
  const isLoading = loadedSrc !== srcString;
  const imgRef = useRef<HTMLImageElement>(null);

  const desktopSrc = cloudflareLoader({ src: srcString, width: DESKTOP_BASE_WIDTH, quality });
  const desktopSrcSet = buildSrcSet(srcString, DESKTOP_BASE_WIDTH, DESKTOP_DENSITIES, quality);
  const mobileSrcSet = srcMobileString
    ? buildSrcSet(srcMobileString, MOBILE_BASE_WIDTH, MOBILE_DENSITIES, quality)
    : undefined;

  // Cached images may fire load before our onLoad is attached — check img.complete
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoadedSrc(srcString);
    }
  }, [srcString]);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {isLoading && (
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          <BannersSkeleton />
        </div>
      )}
      <picture className="block size-full backface-hidden">
        {mobileSrcSet && (
          <source
            media="(max-width: 39.9375rem)"
            srcSet={mobileSrcSet}
            sizes="(min-width: 420px) calc(100vw - 32px), (min-width: 380px) 348px, (min-width: 340px) 308px, 268px"
          />
        )}

        <img
          ref={imgRef}
          {...props}
          src={desktopSrc}
          srcSet={desktopSrcSet}
          sizes="(min-width: 1520px) 1133px, (min-width: 1040px) calc(88.04vw - 188px), (min-width: 420px) calc(100vw - 32px), (min-width: 380px) 348px, (min-width: 340px) 308px, 268px"
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={fetchPriority}
          className={cn(
            'size-full object-cover duration-100 opacity-transition backface-hidden',
            isLoading ? 'opacity-0' : 'opacity-100',
          )}
          onLoad={() => setLoadedSrc(srcString)}
        />
      </picture>
    </div>
  );
};
